import { Alert, Company, Assertion, AttributeType } from "@idw-types/data/reference-data/generic";
import {
    FormattedAlertEventHistoryCause,
    AlertEventHistory,
    FormattedAlertEventHistory,
    FormattedAlertEventHistoryRule,
    AlertEventHistoryCause
} from "@idw-types/data/alert-event-history";
import { RpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { SimpleAxnWorkflow } from "@idw-types/data/reference-data/axn";

export const formatAlertEventHistories = (
    alertEventHistories: Array<AlertEventHistory>,
    alerts: Array<Alert>,
    companies: Array<Company>,
    rpServices: Array<RpServiceConfiguration>,
    workflows: Array<SimpleAxnWorkflow>,
    assertions: Array<Assertion>,
    attributeTypes: Array<AttributeType>
): Array<FormattedAlertEventHistory> => {
    return alertEventHistories.map(history => formatAlertEventHistory(
        history,
        alerts,
        companies,
        rpServices,
        workflows,
        assertions,
        attributeTypes
    ));
};

export const formatAlertEventHistory = (
    alertEventHistory: AlertEventHistory,
    alerts: Array<Alert>,
    companies: Array<Company>,
    rpServices: Array<RpServiceConfiguration>,
    workflows: Array<SimpleAxnWorkflow>,
    assertions: Array<Assertion>,
    attributeTypes: Array<AttributeType>
): FormattedAlertEventHistory => {
    const alert = alerts.find(alert => alert.id === alertEventHistory.alertID);
    const company = companies.find(company => company.id === alertEventHistory.companyID);

    let dateSent = null;
    if (alertEventHistory.dateSent) {
        dateSent = new Date(parseInt(alertEventHistory.dateSent));
    }

    let dateTriggered = null;
    if (alertEventHistory.dateTriggered) {
        dateTriggered = new Date(parseInt(alertEventHistory.dateTriggered));
    }

    let cause: FormattedAlertEventHistoryCause = null;
    if (alertEventHistory.cause) {
        try {
            const parsedJSON = JSON.parse(alertEventHistory.cause) as AlertEventHistoryCause;
            const rpServiceIds: Array<number> = (parsedJSON.rpServiceIDs || []).filter((x: number) => x);
            let rpService: RpServiceConfiguration = null;
            if (rpServiceIds.length > 0) {
                rpService = rpServices.find(service => +service.id === rpServiceIds[0]);
            }
            const workflowIds: Array<number> = (parsedJSON.workflowIDs || []).filter((x: number) => x);
            let workflow: SimpleAxnWorkflow = null;
            if (workflowIds.length > 0) {
                workflow = workflows.find(w => +w.id === workflowIds[0]);
            }
            const assertionIds: Array<number> = (parsedJSON.assertionIDs || []).filter((x: number) => x);
            let assertion: Assertion = null;
            if (assertionIds.length > 0) {
                assertion = assertions.find(a => +a.id === assertionIds[0]);
            }
            const scoreAttributeIds: Array<number> = (parsedJSON.scoreAttributeIDs || []).filter((x: number) => x);
            let scoreAttribute: AttributeType = null;
            if (scoreAttributeIds.length > 0) {
                scoreAttribute = attributeTypes.find(at => +at.id === scoreAttributeIds[0]);
            }

            cause = {
                apAssertionIDs: parsedJSON.apAssertionIDs || [],
                asis: parsedJSON.asis || [],
                assertion: assertion,
                assertionIDs: parsedJSON.assertionIDs || [],
                credentials: (parsedJSON.credentials || []),
                dates: parsedJSON.dates || [],
                ipAddresses: parsedJSON.ipAddresses || [],
                policyIDs: parsedJSON.policyIDs || [],
                rpService: rpService,
                scoreAttribute: scoreAttribute,
                scoreAttributeIDs: parsedJSON.scoreAttributeIDs || [],
                serviceOfferingIDs: parsedJSON.serviceOfferingIDs || [],
                userMBUNs: parsedJSON.userMBUNs || [],
                values: parsedJSON.values || [],
                workflow: workflow,
            }
        } catch (error) {
            console.error(`Error parsing cause JSON: `, error);
        }
    }

    let rule: FormattedAlertEventHistoryRule = null;
    if (alertEventHistory.rule) {
        try {
            const parsedJSON = JSON.parse(alertEventHistory.rule);
            rule = {
                apAssertionIDs: parsedJSON.apAssertionIDs || [],
                assertionStatuses: parsedJSON.assertionStatuses || [],
                verificationServiceIDs: parsedJSON.verificationServiceIDs || []
            };
        } catch (error) {
            console.error("Error parsing rule JSON:", error);
        }
    }

    return {
        alert: alert,
        alertTriggerId: alertEventHistory.alertTriggerID,
        cause: cause,
        company: company,
        id: alertEventHistory.id,
        rule: rule,
        dateSent: dateSent,
        dateTriggered: dateTriggered
    };
};
